import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

import { updatePrimaryCmmdTask } from "~/environments/primary";
import { cmmdWorkIntakeResource, readCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import type { WorkItem } from "./homebase/unifiedWorkItems";
import { getDisplayStatus } from "./command-center/statusConfig";
import type { TaskMutationPatch } from "./command-center/TaskDetailPanel";
import { sanitizeCmmdTaskSyncError, type ManualCommandTask } from "./NoActiveThreadState.logic";
import { runCmmdSync } from "../hooks/useCmmdSync";
import type { StartThreadPromptState } from "./NoActiveThreadStateDialogs";

export interface UseNoActiveThreadTaskMutationsInput {
  readonly workItemById: ReadonlyMap<string, WorkItem>;
  readonly setManualTasks: Dispatch<SetStateAction<readonly ManualCommandTask[]>>;
  readonly setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  readonly setStartThreadPrompt: Dispatch<SetStateAction<StartThreadPromptState | null>>;
}

export function useNoActiveThreadTaskMutations(input: UseNoActiveThreadTaskMutationsInput) {
  const { workItemById, setManualTasks, setSelectedItemId, setStartThreadPrompt } = input;

  const handleUpdateTask = useCallback(
    async (itemId: string, patch: TaskMutationPatch) => {
      if (itemId.startsWith("manual:")) {
        const nextStatus = patch.status;
        if (nextStatus) {
          const updatedAt = new Date().toISOString();
          setManualTasks((existing) =>
            existing.map((task) =>
              task.id === itemId ? { ...task, status: nextStatus, updatedAt } : task,
            ),
          );
        }
        return;
      }

      if (!itemId.startsWith("cmmd:")) return;
      const rawTaskId = itemId.slice(5);
      const previousTasks = readCmmdWorkIntakeSnapshot().tasks;
      const taskPatch = {
        ...(patch.status !== undefined ? { status: patch.status } : {}),
        ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
        ...(patch.dueDate !== undefined ? { dueDate: patch.dueDate } : {}),
        ...(patch.assigneeId !== undefined ? { assigneeId: patch.assigneeId } : {}),
        updatedAt: new Date().toISOString(),
      };
      cmmdWorkIntakeResource.patchTask(rawTaskId, taskPatch);

      try {
        await updatePrimaryCmmdTask({
          taskId: rawTaskId,
          ...(patch.status !== undefined ? { status: patch.status } : {}),
          ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
          ...(patch.dueDate !== undefined ? { dueDate: patch.dueDate } : {}),
          ...(patch.assigneeId !== undefined ? { assigneeId: patch.assigneeId } : {}),
        });
        await runCmmdSync({ forceFetch: true });
        cmmdWorkIntakeResource.patchTask(rawTaskId, taskPatch);
      } catch (error) {
        cmmdWorkIntakeResource.replaceTasks(previousTasks);
        const message = sanitizeCmmdTaskSyncError(error);
        cmmdWorkIntakeResource.setBackgroundRefreshError(message);
        throw error;
      }
    },
    [setManualTasks],
  );

  const handleUpdateTaskStatus = useCallback(
    (
      itemId: string,
      status: CmmdTaskStatus,
      options?: {
        readonly promptStartThread?: boolean;
      },
    ) => {
      const existingItem = workItemById.get(itemId) ?? null;
      const fromStatus = existingItem ? getDisplayStatus(existingItem) : null;
      const updatePromise = handleUpdateTask(itemId, { status });
      const shouldPrompt =
        (options?.promptStartThread ?? true) && status === "in_progress" && fromStatus === "todo";
      if (shouldPrompt) {
        const title = existingItem?.title ?? "this task";
        setStartThreadPrompt({ itemId, title });
      }
      return updatePromise;
    },
    [handleUpdateTask, setStartThreadPrompt, workItemById],
  );

  const handleCreateTask = useCallback(
    (input: { readonly title: string; readonly status: CmmdTaskStatus }) => {
      const title = input.title.trim();
      if (title.length === 0) return;
      const nowIso = new Date().toISOString();
      const taskId = `manual:${crypto.randomUUID()}`;
      const next: ManualCommandTask = {
        id: taskId,
        title,
        status: input.status,
        projectEnvironmentId: null,
        projectId: null,
        projectName: null,
        cmmdProjectId: null,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      setManualTasks((existing) => [next, ...existing]);
      setSelectedItemId(taskId);
    },
    [setManualTasks, setSelectedItemId],
  );

  const handleDeleteTask = useCallback(
    (itemId: string) => {
      if (!itemId.startsWith("manual:")) return;
      setManualTasks((existing) => existing.filter((task) => task.id !== itemId));
      setSelectedItemId((selected) => (selected === itemId ? null : selected));
    },
    [setManualTasks, setSelectedItemId],
  );

  return {
    handleUpdateTask,
    handleUpdateTaskStatus,
    handleCreateTask,
    handleDeleteTask,
  };
}
